import { useTonAddress, useTonConnectUI } from "@tonconnect/ui-react"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { parseAxiosError } from "@/helpers/parseAxiosError"
import { captureException } from "@/helpers/sentry"
import { showToast } from "@/helpers/toast"
import { useAppDispatch } from "@/store"
import { balanceEndpoints } from "@/store/apis/balanceAPI"
import {
  DEPOSIT_TOKEN,
  useCreateDepositInvoiceMutation
} from "@/store/apis/depositAPI"
import styles from "./DepositTab.module.scss"
import { isUserRejectedError } from "./helpers"
import { DepositPresetChip, TonAmountInput } from "./ui"
import { DEPOSIT_PRESETS } from "./values"

export const DepositTab: FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [tonConnectUI] = useTonConnectUI()
  const address = useTonAddress()
  const [createDepositInvoice, { isLoading }] =
    useCreateDepositInvoiceMutation()
  const [amount, setAmount] = useState<number | null>(null)

  const handleDeposit = async () => {
    if (amount === null || amount <= 0) return

    if (!address) {
      await tonConnectUI.openModal()
      return
    }

    try {
      const invoice = await createDepositInvoice({
        address,
        token: DEPOSIT_TOKEN.TON,
        amount
      }).unwrap()

      await tonConnectUI.sendTransaction({
        validUntil: invoice.validUntil,
        messages: invoice.messages
      })

      // Deliberate optimistic patch: the backend credits deposits via a slow
      // cron job (no webhook yet), so we intentionally do NOT set
      // `invalidatesTags: [BALANCE]` on createDepositInvoice — that would
      // trigger a refetch and roll the balance back before the cron catches
      // up. This is a temporary UX compromise until a confirmation event is
      // available server-side.
      dispatch(
        balanceEndpoints.util.updateQueryData(
          "getBalance",
          undefined,
          (draft) => {
            draft.ton += amount
          }
        )
      )

      showToast.info(t("wallet.deposit.toast.pending"))
      setAmount(null)
    } catch (err) {
      if (isUserRejectedError(err)) return

      const parsedErr = parseAxiosError(err)
      if (parsedErr.message) {
        showToast.error(parsedErr.message)
        captureException(err, { feature: "deposit", stage: "invoice" })
        return
      }

      showToast.error(t("wallet.deposit.toast.txError"))
      captureException(err, { feature: "deposit", stage: "tx" })
    }
  }

  return (
    <div className={styles.DepositTab}>
      <div className={styles.DepositTab_card}>
        <div className={styles.DepositTab_title}>
          {t("wallet.deposit.amountLabel")}
        </div>
        <div className={styles.DepositTab_description}>
          {t("wallet.deposit.description")}
        </div>
        <TonAmountInput
          value={amount}
          onChange={setAmount}
          onSubmit={handleDeposit}
          isLoading={isLoading}
          isConnected={!!address}
        />
        <div className={styles.DepositTab_presets}>
          {DEPOSIT_PRESETS.map((preset) => (
            <DepositPresetChip
              key={preset}
              amount={preset}
              isActive={amount === preset}
              onClick={() => setAmount(preset)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
